import { useQuery } from '@tanstack/react-query';
import { fetchFeaturedContent } from '../../api/sportsbook-api';
import { RefetchInterval, StaleTime } from '../../sportsbook-constants';
import { FeaturedContentDto, FeaturedContentTypeDto } from '../../api/sbapi/sbapi-api-model';
import { useCallback } from 'react';
import { SPORTSBOOK_QUERY_KEY } from '../queries-constants';

type Options<TData> = {
	select?: (data: FeaturedContentDto[]) => TData;
	staleTime?: StaleTime;
	refetchInterval?: RefetchInterval;
};

/**
 * Hook for querying featured content.
 */
const useQueryFeaturedContent = <TData = FeaturedContentDto[]>(
	types: FeaturedContentTypeDto[],
	options?: Options<TData>
) =>
	useQuery({
		queryKey: [SPORTSBOOK_QUERY_KEY, 'featured-content', types],
		queryFn: () => fetchFeaturedContent(types),
		staleTime: options?.staleTime ?? StaleTime.ZERO,
		refetchInterval: options?.refetchInterval,
		select: options?.select,
		meta: {
			errorMessage: `Failed to fetch featured content for types: ${types.join(', ')}`
		}
	});

/**
 * Hook for querying a single featured content item.
 */
export const useQuerySingleFeaturedContent = (
	type: FeaturedContentTypeDto,
	options?: Omit<Options<FeaturedContentDto>, 'select'>
) => {
	const selectSingleFeaturedContentFromList = useCallback(
		(data: FeaturedContentDto[]) => data.find(featuredContent => featuredContent.type === type),
		[type]
	);

	return useQueryFeaturedContent([type], {
		...options,
		select: selectSingleFeaturedContentFromList
	});
};

export default useQueryFeaturedContent;
